import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth-store'
import { triggerRevalidation } from '@/lib/utils'
import type { Json } from '@/types/supabase'

export interface NavLinkConfig {
  id: string
  href: string
  label: string
  enabled: boolean
  external?: boolean
}

export interface NavConfig {
  links: NavLinkConfig[]
  show_cheki_button: boolean
}

export interface FooterConfig {
  links: NavLinkConfig[]
}

export const DEFAULT_NAV_LINKS: NavLinkConfig[] = [
  { id: 'about',   href: '/about',   label: 'About',   enabled: true },
  { id: 'members', href: '/members', label: 'Members', enabled: true },
  { id: 'music',   href: '/music',   label: 'Music',   enabled: true },
  { id: 'events',  href: '/events',  label: 'Events',  enabled: true },
  { id: 'shop',    href: '/shop',    label: 'Shop',    enabled: true },
]

export const DEFAULT_FOOTER_LINKS: NavLinkConfig[] = [
  { id: 'about',   href: '/about',   label: 'About',   enabled: true },
  { id: 'members', href: '/members', label: 'Members', enabled: true },
  { id: 'music',   href: '/music',   label: 'Music',   enabled: true },
  { id: 'events',  href: '/events',  label: 'Events',  enabled: true },
  { id: 'shop',    href: '/shop',    label: 'Shop',    enabled: true },
  { id: 'cheki',   href: '/cheki',   label: 'Cheki',   enabled: true },
]

export function useNavConfig() {
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useQuery({
    queryKey: ['nav-config', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tenants')
        .select('nav_config, footer_config')
        .eq('id', tenantId!)
        .single()
      if (error) throw error
      return data as unknown as { nav_config: NavConfig | null; footer_config: FooterConfig | null }
    },
  })
}

export function useUpdateNavConfig() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async (navConfig: NavConfig) => {
      const { data, error } = await supabase
        .from('tenants')
        .update({ nav_config: navConfig as unknown as Json })
        .eq('id', tenantId!)
        .select('id, nav_config')
        .single()
      if (error) throw error
      return data
    },
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: ['nav-config', tenantId] })
      await triggerRevalidation('tenant-theme')
    },
  })
}

export function useUpdateFooterConfig() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async (footerConfig: FooterConfig) => {
      const { data, error } = await supabase
        .from('tenants')
        .update({ footer_config: footerConfig as unknown as Json })
        .eq('id', tenantId!)
        .select('id, footer_config')
        .single()
      if (error) throw error
      return data
    },
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: ['nav-config', tenantId] })
      await triggerRevalidation('tenant-theme')
    },
  })
}
